'use client'

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { format } from 'date-fns'
import { useTranslations } from 'next-intl'
import { useLocale } from 'next-intl'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  BriefcaseIcon,
  CheckCircleIcon,
  GlobeIcon,
  LockIcon,
  MailIcon,
  PhoneIcon,
  PlusCircleIcon,
  SaveIcon,
  ShieldIcon,
  ShieldUserIcon,
  UserIcon,
  XIcon,
  CalendarIcon
} from 'lucide-react'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { NumberInput } from '@/components/ui/number-input'
import { PasswordInput } from '@/components/ui/password-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Combobox } from '@/components/ui/combobox'
import { Separator } from '@/components/ui/separator'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { DatePicker } from '@/components/ui/date-picker'
import { PermissionSelector, UnsavedChangesGuardDialog } from '@/components/partials'
import { useGroups } from '@/hooks/use-groups'
import { usePermissions, useUnsavedChangesGuard } from '@/hooks'
import { useScrollToError } from '@/hooks'
import { FormModeEnum, GenderEnum, RoleEnum } from '@/enums'
import { setValidationErrors } from '@/lib/utils'
import { UserType } from '@/types/user'
import { useMetaStore, useMeStore } from '@/store'

interface PropsType {
  user?: UserType
  onSubmit: (data: any) => void
  onSubmitAndNew?: (data: any) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export interface UserFormRef {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type FormData = {
  firstname: string
  lastname: string
  gender: GenderEnum
  birthDate: string
  email: string
  timezone: string
  discountRate: number
  currencyCode: string
  company: string
  phone: string
  password?: string
  passwordConfirm?: string
  emailVerified: boolean
  blocked: boolean
  approved: boolean
  roles: RoleEnum[]
  groups: string[]
  permissionActions: string[]
}

const UserForm = forwardRef<UserFormRef, PropsType>(
  ({ user, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const locale = useLocale()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    const { meState } = useMeStore()
    const currentUser = meState.response
    const { data: groupsData } = useGroups({ size: 999999 })

    // Check if the current user is ROOT - only ROOT users can assign ROOT role
    const isCurrentUserRoot = currentUser?.roles?.includes(RoleEnum.ROOT)

    // Available roles based on the current user
    // If a role is ROOT and the current user is not ROOT, filter it out
    const availableRoles = Object.values(RoleEnum).filter(role => !(role === RoleEnum.ROOT && !isCurrentUserRoot))

    // State for selected roles to fetch permissions
    const [selectedRoles, setSelectedRoles] = useState<RoleEnum[]>(
      mode === FormModeEnum.CREATE ? [RoleEnum.USER] : user?.roles || [RoleEnum.USER]
    )

    // Fetch permissions based on selected roles (only if roles are selected)
    const { data: permissions, isLoading: isLoadingPermissions } = usePermissions(
      {
        roles: selectedRoles.join(',')
      },
      {
        enabled: selectedRoles.length > 0
      }
    )

    const getSchema = () => {
      const baseSchema = {
        firstname: z.string().min(1, t('not_blank')),
        lastname: z.string().min(1, t('not_blank')),
        gender: z.enum([GenderEnum.MALE, GenderEnum.FEMALE, GenderEnum.DIVERSE, GenderEnum.UNKNOWN]),
        birthDate: z.string().min(1, t('not_blank')),
        email: z.email(t('invalid_email')),
        timezone: z.string().min(1, t('not_blank')),
        discountRate: z
          .number()
          .min(0, t('min_value', { value: 0 }))
          .max(100, t('max_value', { value: 100 })),
        currencyCode: z.string(),
        company: z.string(),
        phone: z.string(),
        emailVerified: z.boolean(),
        blocked: z.boolean(),
        approved: z.boolean(),
        roles: z.array(z.enum([RoleEnum.ROOT, RoleEnum.ADMIN, RoleEnum.USER])).min(1, t('min_value', { value: 1 })),
        groups: z.array(z.string()),
        permissionActions: z.array(z.string())
      }

      if (mode === FormModeEnum.CREATE) {
        return z
          .object({
            ...baseSchema,
            password: z
              .string()
              .min(6, t('min_value', { value: 6 }))
              .max(32, t('max_value', { value: 32 })),
            passwordConfirm: z
              .string()
              .min(6, t('min_value', { value: 6 }))
              .max(32, t('max_value', { value: 32 }))
          })
          .refine(data => data.password === data.passwordConfirm, {
            message: t('password_mismatch'),
            path: ['passwordConfirm']
          })
      } else {
        return z
          .object({
            ...baseSchema,
            password: z.string().optional(),
            passwordConfirm: z.string().optional()
          })
          .refine(
            data => {
              if (data.password && data.password.length > 0) {
                return data.password === data.passwordConfirm && data.password.length >= 8
              }
              return true
            },
            {
              message: 'passwords_must_match',
              path: ['passwordConfirm']
            }
          )
      }
    }

    const form = useForm<FormData>({
      resolver: zodResolver(getSchema()),
      defaultValues:
        mode === FormModeEnum.CREATE
          ? {
              firstname: '',
              lastname: '',
              gender: GenderEnum.UNKNOWN,
              birthDate: '',
              email: '',
              timezone: '',
              discountRate: 0,
              currencyCode: '',
              company: '',
              phone: '',
              password: '',
              passwordConfirm: '',
              emailVerified: false,
              blocked: false,
              approved: false,
              roles: [RoleEnum.USER],
              groups: [],
              permissionActions: []
            }
          : {
              firstname: user?.firstname || '',
              lastname: user?.lastname || '',
              gender: user?.gender || GenderEnum.UNKNOWN,
              birthDate: user?.birthDate || '',
              email: user?.email || '',
              timezone: user?.timezone || '',
              discountRate: user?.discountRate ?? 0,
              currencyCode: user?.currencyCode || '',
              company: user?.company || '',
              phone: user?.phone || '',
              password: '',
              passwordConfirm: '',
              emailVerified: !!user?.emailVerifiedAt,
              blocked: false,
              approved: !!user?.approvedAt,
              roles: user?.roles || [RoleEnum.USER],
              groups: user?.groups?.map(group => group.id) || [],
              permissionActions: user?.specificPermissions
                ? user.specificPermissions.flatMap(p => p.actions.map(a => a.id))
                : []
            }
    })

    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    // Watch role changes and update permissions
    const watchRoles = form.watch('roles')
    useEffect(() => {
      if (JSON.stringify(watchRoles) !== JSON.stringify(selectedRoles)) {
        setSelectedRoles(watchRoles)
        // Reset permission actions when roles change
        form.setValue('permissionActions', [])
      }
    }, [watchRoles, selectedRoles, form])

    const handleSubmit = (data: FormData) => {
      // Convert form data to API format
      const submitData = {
        ...data,
        password: data.password || undefined,
        passwordConfirm: data.passwordConfirm || undefined
      }

      // Remove empty password fields for update
      if (mode === FormModeEnum.UPDATE && (!submitData.password || submitData.password.length === 0)) {
        delete submitData.password
        delete submitData.passwordConfirm
      }

      onSubmit(submitData)
    }

    const handleSubmitAndNew = (data: FormData) => {
      // Convert form data to API format
      const submitData = {
        ...data,
        password: data.password || undefined,
        passwordConfirm: data.passwordConfirm || undefined
      }

      // Remove empty password fields for update
      if (mode === FormModeEnum.UPDATE && (!submitData.password || submitData.password.length === 0)) {
        delete submitData.password
        delete submitData.passwordConfirm
      }

      onSubmitAndNew?.(submitData)
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    const handleRoleToggle = (role: RoleEnum, checked: boolean) => {
      const currentRoles = form.getValues('roles')
      if (checked) {
        form.setValue('roles', [...currentRoles, role])
      } else {
        form.setValue(
          'roles',
          currentRoles.filter(r => r !== role)
        )
      }
    }

    const handleGroupToggle = (groupId: string, checked: boolean) => {
      const currentGroups = form.getValues('groups')
      if (checked) {
        form.setValue('groups', [...currentGroups, groupId])
      } else {
        form.setValue(
          'groups',
          currentGroups.filter(id => id !== groupId)
        )
      }
    }

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserIcon className="h-5 w-5" />
              {mode === FormModeEnum.CREATE ? t('create_user') : t('edit_user')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(
                  isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
                  scrollToFirstError
                )}
                className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <FormField
                    control={form.control}
                    name="firstname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>{t('firstname')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('enter_firstname')} {...field} autoFocus />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>{t('lastname')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('enter_lastname')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <BriefcaseIcon className="h-4 w-4" />
                          {t('company')}
                        </FormLabel>
                        <FormControl>
                          <Input placeholder={t('enter_company')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <PhoneIcon className="h-4 w-4" />
                          {t('phone')}
                        </FormLabel>
                        <FormControl>
                          <PhoneInput
                            international
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required className="flex items-center gap-1">
                          <MailIcon className="h-4 w-4" />
                          {t('email')}
                        </FormLabel>
                        <FormControl>
                          <Input type="email" placeholder={t('enter_email')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel required className="flex items-center gap-1">
                          <GlobeIcon className="h-4 w-4" />
                          {t('timezone')}
                        </FormLabel>
                        <FormControl>
                          <Combobox
                            options={(metaState.response?.timezones || []).map(timezone => ({
                              label: timezone.id,
                              value: timezone.id,
                              description: timezone.offset,
                              searchValue: `${timezone.id} ${timezone.offset}`
                            }))}
                            value={field.value}
                            onValueChange={field.onChange}
                            icon={<GlobeIcon className="h-4 w-4" />}
                            placeholder={t('select')}
                            searchPlaceholder={t('search')}
                            emptyText={t('not_found')}
                            clearable={true}
                            error={!!fieldState.error}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('gender')}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl className="w-full">
                            <SelectTrigger>
                              <SelectValue placeholder={t('select')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(GenderEnum).map(gender => (
                              <SelectItem key={gender} value={gender}>
                                {t(gender)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="birthDate"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <CalendarIcon className="h-4 w-4" />
                          {t('birth_date')}
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            value={field.value ? new Date(field.value) : undefined}
                            onChange={date => field.onChange(date ? format(date, 'yyyy-MM-dd') : undefined)}
                            placeholder={t('select_date')}
                            isInvalid={!!fieldState.error}
                            locale={locale}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="discountRate"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">{t('discount_rate')}</FormLabel>
                        <FormControl>
                          <NumberInput
                            min={0}
                            max={100}
                            stepper={0.5}
                            decimalScale={2}
                            fixedDecimalScale
                            suffix="%"
                            value={field.value}
                            onValueChange={val => field.onChange(val ?? 0)}
                            error={!!fieldState.error}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="currencyCode"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t('currency')}</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl className="w-full">
                            <SelectTrigger className={fieldState.error ? 'border-destructive' : ''}>
                              <SelectValue placeholder={t('select')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(metaState.response?.currencies || []).map(currency => (
                              <SelectItem key={currency.code} value={currency.code}>
                                {currency.symbol} {currency.code} — {currency.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 text-lg font-medium">
                    <LockIcon className="h-4 w-4" />
                    {mode === FormModeEnum.CREATE ? t('password') : t('change_password')}
                    {mode === FormModeEnum.UPDATE && (
                      <span className="text-muted-foreground text-sm font-normal">({t('optional')})</span>
                    )}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required={mode === FormModeEnum.CREATE}>{t('password')}</FormLabel>
                          <FormControl>
                            <PasswordInput
                              placeholder={mode === FormModeEnum.CREATE ? t('enter_password') : t('enter_new_password')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="passwordConfirm"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required={mode === FormModeEnum.CREATE}>{t('password_confirm')}</FormLabel>
                          <FormControl>
                            <PasswordInput placeholder={t('confirm_password')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
                  <div className="space-y-4">
                    <h3 className="flex items-center gap-2 text-lg font-medium">
                      <ShieldIcon className="h-4 w-4" />
                      {t('roles')}
                    </h3>
                    <FormField
                      control={form.control}
                      name="roles"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex flex-wrap gap-2">
                            {availableRoles.map(role => (
                              <div key={role} className="flex items-center space-x-2">
                                <Checkbox
                                  id={role}
                                  checked={field.value.includes(role)}
                                  onCheckedChange={checked => handleRoleToggle(role, checked as boolean)}
                                />
                                <label
                                  htmlFor={role}
                                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                  <Badge variant="outline">{t(role)}</Badge>
                                </label>
                              </div>
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="space-y-4">
                    <h3 className="flex items-center gap-2 text-lg font-medium">
                      <ShieldUserIcon className="h-4 w-4" />
                      {t('groups')}
                    </h3>
                    <FormField
                      control={form.control}
                      name="groups"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex flex-wrap gap-2">
                            {groupsData?.items?.map(group => (
                              <div key={group.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`group-${group.id}`}
                                  checked={field.value.includes(group.id)}
                                  onCheckedChange={checked => handleGroupToggle(group.id, checked as boolean)}
                                />
                                <label
                                  htmlFor={`group-${group.id}`}
                                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                  <Badge variant="outline">{group.name}</Badge>
                                </label>
                              </div>
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="space-y-4">
                    <h3 className="flex items-center gap-2 text-lg font-medium">
                      <CheckCircleIcon className="h-4 w-4" />
                      {t('account_settings')}
                    </h3>
                    <div className="space-y-3">
                      <FormField
                        control={form.control}
                        name="emailVerified"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center space-y-0">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>{t('email_verified')}</FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="blocked"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center space-y-0">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>{t('blocked')}</FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="approved"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center space-y-0">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>{t('approved')}</FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* User Specific Permissions - Collapsible */}
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="user-permissions" className="border-input rounded-lg border border-b! px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <ShieldIcon className="h-4 w-4" />
                        <span className="text-lg font-medium">{t('user_specific_permissions')}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pt-4 pb-4">
                      <FormField
                        control={form.control}
                        name="permissionActions"
                        render={({ field }) => (
                          <FormItem>
                            <PermissionSelector
                              permissions={permissions}
                              selectedActionIds={field.value}
                              onActionIdsChange={field.onChange}
                              isLoading={isLoadingPermissions}
                              emptyMessage={t('select')}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <Separator />
                <div className="flex items-center justify-between gap-3 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={isLoading || form.formState.isSubmitting}>
                    <XIcon />
                    <span className="hidden sm:inline">{t('cancel')}</span>
                  </Button>
                  <div className="flex items-center gap-2">
                    {isCreateModeWithSubmitAndNew && (
                      <Button
                        type="button"
                        onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                        disabled={isLoading || form.formState.isSubmitting}>
                        <SaveIcon />
                        <span className="hidden sm:inline">
                          {isLoading || form.formState.isSubmitting ? t('saving') : t('save')}
                        </span>
                      </Button>
                    )}
                    <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                      {isCreateModeWithSubmitAndNew ? <PlusCircleIcon /> : <SaveIcon />}
                      {isLoading || form.formState.isSubmitting
                        ? t('saving')
                        : isCreateModeWithSubmitAndNew
                          ? t('save_and_add_new')
                          : t(mode)}
                    </Button>
                  </div>
                </div>
                <UnsavedChangesGuardDialog
                  open={isNavigationBlocked}
                  onConfirm={confirmNavigation}
                  onCancel={cancelNavigation}
                />
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    )
  }
)

UserForm.displayName = 'UserForm'

export default UserForm
